import { getTemplateGroup, getTemplateResolutionOption, getTemplateTask } from './ScanTemplateGetters';
import { updateTemplateItemHelper } from './ScanTemplateLib';
import {
  ScanTemplateRoot,
  UpdateScanTemplate,
  UpdateScanTemplateGroup,
  UpdateScanTemplateLineItem,
  UpdateScanTemplatePartWithContext,
  UpdateScanTemplateResolutionOption,
  UpdateScanTemplateTask,
} from './ScanTemplateSchemas';

export const updateTemplateLineItem = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOptionId: string,
  lineItemId: string,
  updates: UpdateScanTemplateLineItem,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const resolutionOption = getTemplateResolutionOption(template, groupId, taskId, resolutionOptionId);
  if (!resolutionOption) return { ...template, updatedAt: now };

  // Find the line item and make sure it exists
  const lineItemIndex = resolutionOption.lineItemTemplates?.findIndex(
    li => li.id === lineItemId,
  ) ?? -1;

  if (lineItemIndex === -1) {
    // Just update the resolution option's timestamp but don't modify the line items
    return updateTemplateResolutionOption(template, groupId, taskId, resolutionOptionId, { updatedAt: now }, now);
  }

  // Clone the line items array and update the specific one
  const updatedLineItems = resolutionOption.lineItemTemplates ? [...resolutionOption.lineItemTemplates] : [];
  updatedLineItems[lineItemIndex] = updateTemplateItemHelper(updatedLineItems[lineItemIndex], updates, now);

  return updateTemplateResolutionOption(
    template,
    groupId,
    taskId,
    resolutionOptionId,
    { lineItemTemplates: updatedLineItems },
    now,
  );
};

export const updateTemplateResolutionOption = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  resolutionOptionId: string,
  updates: UpdateScanTemplateResolutionOption,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const task = getTemplateTask(template, groupId, taskId);
  if (!task) return { ...template, updatedAt: now };

  // Find the resolution option and make sure it exists
  const resolutionOptionIndex = task.optionTemplates?.findIndex(
    r => r.id === resolutionOptionId,
  ) ?? -1;

  if (resolutionOptionIndex === -1) {
    // Just update the task's timestamp but don't modify the resolution options
    return updateTemplateTask(template, groupId, taskId, { updatedAt: now }, now);
  }

  // Clone the resolution options array and update the specific one
  const updatedResolutionOptions = task.optionTemplates ? [...task.optionTemplates] : [];
  updatedResolutionOptions[resolutionOptionIndex] = updateTemplateItemHelper(updatedResolutionOptions[resolutionOptionIndex], updates, now);

  return updateTemplateTask(
    template,
    groupId,
    taskId,
    { optionTemplates: updatedResolutionOptions },
    now,
  );
};

export const updateTemplateTask = (
  template: ScanTemplateRoot,
  groupId: string | undefined,
  taskId: string,
  updates: UpdateScanTemplateTask,
  now: number = Date.now(),
): ScanTemplateRoot => {
  if (template.taskTemplatesById && template.taskTemplatesById[taskId]) {
    // normalized template
    return {
      ...template,
      taskTemplatesById: {
        ...template.taskTemplatesById,
        [taskId]: updateTemplateItemHelper(template.taskTemplatesById[taskId], updates, now),
      },
    };
  }

  // denormalized template
  const group = groupId ? getTemplateGroup(template, groupId) : undefined;
  if (!groupId || !group) return { ...template, updatedAt: now };

  // Find the task and make sure it exists
  const taskIndex = group.taskTemplates?.findIndex(t => t.id === taskId) ?? -1;
  if (taskIndex === -1) {
    // Just update the group's timestamp but don't modify the tasks
    return updateTemplateGroup(template, groupId, { updatedAt: now }, now);
  }

  // Clone the tasks array and update the specific task
  const updatedTasks = group.taskTemplates ? [...group.taskTemplates] : [];
  updatedTasks[taskIndex] = updateTemplateItemHelper(updatedTasks[taskIndex], updates, now);

  return updateTemplateGroup(
    template,
    groupId,
    { taskTemplates: updatedTasks },
    now,
  );
};

export const updateTemplateGroup = (
  template: ScanTemplateRoot,
  groupId: string,
  updates: UpdateScanTemplateGroup,
  now: number = Date.now(),
): ScanTemplateRoot => ({
  ...template,
  groupTemplates: template.groupTemplates?.map(g =>
    g.id === groupId
      ? updateTemplateItemHelper(g, updates, now)
      : g
  ),
  updatedAt: now,
});

export const updateScanTemplateRoot = (template: ScanTemplateRoot, updates: UpdateScanTemplate, now: number = Date.now()): ScanTemplateRoot => ({
  ...updateTemplateItemHelper(template, updates, now),
  groupTemplates: updates.groupTemplates ?? template.groupTemplates,
});

/** @deprecated use updateScanTemplateRoot instead */
export const updateScanTemplateData = (template: ScanTemplateRoot, updates: UpdateScanTemplate, now: number = Date.now()): ScanTemplateRoot =>
  updateScanTemplateRoot(template, updates, now);

export const updateScanTemplatePart = (
  template: ScanTemplateRoot,
  updatesWithContext: UpdateScanTemplatePartWithContext,
  now: number = Date.now(),
): ScanTemplateRoot => {
  const { groupId, taskId, optionId, lineItemId, updates } = updatesWithContext;
  if (lineItemId) {
    if (!taskId || !optionId) throw new Error('groupId, taskId and optionId are required');
    return updateTemplateLineItem(template, groupId, taskId, optionId, lineItemId, updates, now);
  }
  if (optionId) {
    if (!taskId) throw new Error('taskId is required');
    return updateTemplateResolutionOption(template, groupId, taskId, optionId, updates, now);
  }
  if (taskId) {
    return updateTemplateTask(template, groupId, taskId, updates, now);
  }
  if (groupId) {
    return updateTemplateGroup(template, groupId, updates, now);
  }
  throw new Error('No groupId, taskId, optionId or lineItemId provided');
};
